import {
	Button,
	Divider,
	Stack,
	Box,
	Typography,
	TextField,
	Alert
} from "@mui/material";
import React, { useContext, useState, useRef } from "react";
import PropTypes from 'prop-types';
import GoogleOAuthChoice from "./Google/GoogleOAuthChoice";
import GoogleIcon from "./Google/GoogleIcon";
import FacebookOAuthChoice from "./Facebook/FacebookOAuthChoice";
import FacebookIcon from "./Facebook/FacebookIcon";
import MicrosoftOAuthChoice from './Microsoft/MicrosoftOAuthChoice';
import { ConfigContext } from '../Config/ConfigContext';
import VerificationEmail from './VerificationEmail';
import useApi from "../hooks/useApi";

Auth.propTypes = {
	LoginHeaderLogo: PropTypes.string,
	signInBlurb: PropTypes.string,
	signInBackgroundColor: PropTypes.string,
	/**
	 * An array of sign-up options to display below the login section.
	 * Each option should include:
	 * - SignUpText: The text to display on the button.
	 * - SignUpUrl: The URL to redirect the user to when clicking the button.
	 */
	signUpOptions: PropTypes.arrayOf(PropTypes.shape({
		SignUpText: PropTypes.string.isRequired,
		SignUpUrl: PropTypes.string.isRequired,
	}))
};

/**
 * Auth component renders the authentication page with options to sign in using
 * Google, Facebook, Microsoft, or email verification.
 * 
 * Additionally, if signUpOptions are provided, renders a section below the login
 * area to allow users to navigate to external sign-up pages.
 *
 * This component also centrally handles and displays authentication error messages
 * returned from API calls via the handleAuthError function. Any child auth flow
 * can invoke this function to surface errors as an MUI Alert within the page.
 *
 * @component
 * @param {Object} props - The component properties.
 * @param {string} props.LoginHeaderLogo - URL for the login header logo.
 * @param {string} props.signInBlurb - Additional blurb or message for sign in.
 * @param {string} props.signInBackgroundColor - Background color for the sign in container.
 * @param {Array<{SignUpText: string, SignUpUrl: string}>} [props.signUpOptions=[]] - Collection of sign-up options to render below the login section.
 * @returns {JSX.Element} The rendered authentication component.
 */
export default function Auth({
	LoginHeaderLogo,
	signInBlurb,
	signInBackgroundColor,
	signUpOptions = [],
}) {
	const [useFacebookOAuth, setUseFacebookOAuth] = useState(false);
	const [useShadowAuth, setUseShadowAuth] = useState(false);
	const { config } = useContext(ConfigContext);
	const api = useApi();

	const googleBtnRef = useRef(null);

	const [email, setEmail] = useState('');
	const [emailError, setEmailError] = useState('');
	/**
	 * Holds the latest authentication error message to display in an MUI Alert.
	 * This is populated by handleAuthError when any auth flow encounters an API error.
	 * @type {string}
	 */
	const [authError, setAuthError] = useState('');

	/**
	 * Callback function to handle successful authentication.
	 * - Clears any existing error messages.
	 * - Reloads the page to reflect authenticated state.
	 */
	const handleAuthSuccess = () => {
		setAuthError('');
        window.location.reload();
	};

	/**
	 * Safely extracts a human-readable error message from various error shapes.
	 *
	 * @param {unknown} err - The error object thrown by an auth API call.
	 * @returns {string} The best-effort error message for display.
	 */
	const extractAuthErrorMessage = (err) => {
		// Common shapes: AxiosError, Fetch error, generic Error, string, or unknown.
		if (!err) return 'An unexpected error occurred. Please try again.';
		// If error is already a string message
		if (typeof err === 'string') return err;
		// Axios-like error responses
		// eslint-disable-next-line no-unsafe-optional-chaining
		if (err.response?.data?.message) return err.response.data.message;
		// eslint-disable-next-line no-unsafe-optional-chaining
		if (err.response?.data?.error) return err.response.data.error;
		// Direct message
		if (err.message) return err.message;
		// Fallback
		return 'An unexpected error occurred. Please try again.';
	};

	/**
	 * Handles authentication errors by extracting a user-friendly message
	 * and updating local state to display it in an MUI Alert.
	 *
	 * Child components should call this function when their auth API call fails.
	 *
	 * @param {unknown} error - The error object from the failed auth attempt.
	 */
	const handleAuthError = (error) => {
		const message = extractAuthErrorMessage(error);
		setAuthError(message);
	};

	/**
	 * Validates the provided email address.
	 *
	 * @param {string} value - The email address to validate.
	 * @returns {boolean} True if the email is valid, false otherwise.
	 */
	const validateEmail = (value) => {
		// Simple email validation regex
		const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return regex.test(value);
	};

	/**
	 * Handles changes to the email input field.
	 *
	 * @param {Object} e - The event object.
	 */
	const handleEmailChange = (e) => {
		const value = e.target.value;
		setEmail(value);
		if (value === '' || validateEmail(value)) {
			setEmailError('');
		} else {
			setEmailError('Invalid email format');
		}
	};

	/**
	 * Handles the email form submission for authentication.
	 * Initiates the "shadow" auth flow through the API and surfaces any API
	 * errors via handleAuthError (displayed as an Alert).
	 *
	 * @returns {Promise<void>}
	 */
	const handleEmailSubmit = async () => {
		// Clear any previous global auth errors before a new attempt.
		setAuthError('');

		// Check if email is valid before proceeding
		if (!email) {
			setEmailError('Email is required');
			return;
		}
		if (!validateEmail(email)) {
			setEmailError('Invalid email format');
			return;
		}
		try {
			await api.auth.init({ params: { signInPlatform: "shadow", email: email } });
			setUseShadowAuth(true);
		} catch (error) {
			// Surface API errors to the UI
			handleAuthError(error);
			// eslint-disable-next-line no-console
			console.error('Error initiating email auth:', error);
		}
	};

	return (
		<React.Fragment>
			<Box
				className="auth-container"
				backgroundColor={signInBackgroundColor || '#ffffff'}
				sx={{ width: '100%', height: '100%' }}
				display="flex"
				alignItems="center"
				justifyContent="center"
				flexDirection="column"
			>

				{LoginHeaderLogo && (
					<Box 
                        display="flex" 
                        alignItems="center" 
                        justifyContent="center" 
                        sx={{ 
                          margin: '20px', 
                          maxWidth: '400px', 
                          width: '100%' 
                        }}>
						<img 
                          style={{ 
                            maxWidth: '100%', 
                            height: '100%' 
                          }} 
                          src={LoginHeaderLogo} 
                          alt="login header" 
                        />
					</Box>
				)}

				{/* Sign-in card containing OAuth and email options */}
				<Box
					padding="20px"
					sx={{
						backgroundColor: 'white',
						width: '100%',
						maxWidth: '335px',
						borderColor: '#E0E0E0',
						borderWidth: '1px',
						borderStyle: 'solid',
						boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
						borderRadius: '20px'
					}}
				>
					<Stack direction="column" alignItems="center" justifyContent="center" gap="4px">
						<Typography sx={{ fontSize: '24px', fontWeight: 800, lineHeight: '28.8px' }}>Sign in</Typography>
						{signInBlurb && (
							<Typography sx={{ color: '#5B626E', fontSize: '14px', lineHeight: '21px', fontWeight: 700, width: '60%', textAlign: 'center' }}>{signInBlurb}</Typography>
						)}
						{/* Global auth error alert shown directly beneath the sign-in header */}
						{authError && (
							<Box sx={{ width: '100%', marginTop: '8px' }}>
								<Alert severity="error">{authError}</Alert>
							</Box>
						)}
					</Stack>
					<Stack gap="16px" sx={{ marginTop: '20px' }} direction="column" justifyContent="center">
						{config.googleOAuth.enabled &&
							<React.Fragment>
								<GoogleOAuthChoice 
                                  parentBtnRef={googleBtnRef} 
                                  setAuthSuccess={handleAuthSuccess}
								  handleAuthError={handleAuthError}
                                />
								<Button
									ref={googleBtnRef}
									sx={{
										color: '#1B1C1E',
										textTransform: 'initial',
										padding: '8px',
										display: 'flex',
										alignItems: 'center',
										gap: '8px',
										fontWeight: '700',
										fontSize: '15px'
									}}
									onClick={() => {
										setUseFacebookOAuth(false);
										// Clear any prior error when the user starts a new attempt
										setAuthError('');
									}}
								>
									<GoogleIcon />
									Continue with Google
								</Button>
							</React.Fragment>
						}
						{config.facebookOAuth.enabled &&
							<Button
								sx={{
									borderRadius: '90px',
									border: '1px solid #5b626E',
									color: '#1B1C1E',
									textTransform: 'initial',
									padding: '8px',
									display: 'flex',
									alignItems: 'center',
									gap: '8px',
									fontWeight: '700',
									fontSize: '15px'
								}}
								onClick={() => {
									setUseFacebookOAuth(true);
									// Clear any prior error when the user starts a new attempt
									setAuthError('');
								}}
							>Continue with Facebook
								<FacebookIcon />
							</Button>}
						{config.microsoftOAuth.enabled &&
							<React.Fragment>
								<MicrosoftOAuthChoice
									setAuthSuccess={handleAuthSuccess}
									handleAuthError={handleAuthError}
								/>
							</React.Fragment>
						}
					</Stack>
					{config.emailAuth.enabled &&
						<React.Fragment>
							<Stack sx={{ margin: '16px 0px 0px' }} gap="16px">
								<Divider />
							</Stack>
							<Stack gap="16px">
								<TextField
									label="Email"
									value={email}
									error={Boolean(emailError)}
									helperText={emailError}
									onChange={handleEmailChange}
									fullWidth
									variant="outlined"
								/>
								<Button
									sx={{
										borderRadius: '90px',
										border: '1px solid #5b626E',
										color: '#1B1C1E',
										textTransform: 'initial',
										padding: '8px',
										display: 'flex',
										alignItems: 'center',
										gap: '8px',
										fontWeight: '700',
										fontSize: '15px'
									}}
									onClick={handleEmailSubmit}
								>Continue</Button>
							</Stack>
						</React.Fragment>
					}
				</Box>

				{/* Sign-up options section rendered below the sign-in card */}
				{Array.isArray(signUpOptions) && signUpOptions.length > 0 && (
					<Box
						sx={{
							width: '100%',
							maxWidth: '335px',
							marginTop: '16px'
						}}
					>
						<Stack direction="column" gap="12px" alignItems="center">
							<Typography sx={{ color: '#1B1C1E', fontSize: '14px', lineHeight: '21px', fontWeight: 700, textAlign: 'center' }}>
								Don't have an account? Sign up with one of the options below
							</Typography>
							<Stack gap="8px" sx={{ width: '100%' }}>
								{signUpOptions.map((option, idx) => (
									<Button
										key={`${option.SignUpText}-${idx}`}
										sx={{
											borderRadius: '90px',
											border: '1px solid #5b626E',
											color: '#1B1C1E',
											textTransform: 'initial',
											padding: '8px',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											fontWeight: '700',
											fontSize: '15px',
											width: '100%'
										}}
										// Redirect user to the sign-up URL for the selected option.
										onClick={() => {
											if (option && option.SignUpUrl) {
												window.location.assign(option.SignUpUrl);
											}
										}}
									>
										{option.SignUpText}
									</Button>
								))}
							</Stack>
						</Stack>
					</Box>
				)}
			</Box>
			{useShadowAuth && <VerificationEmail />}
			{useFacebookOAuth && <FacebookOAuthChoice setAuthSuccess={handleAuthSuccess} handleAuthError={handleAuthError} />}
		</React.Fragment>
	);
}